'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';

// Dark-zone detection uses the explicit data-header-dark="true" attribute on
// hero sections and other known-dark containers. The previous approach scanned
// every DOM element with getComputedStyle() on each scroll frame — a massive
// forced-reflow source. Scoping to only the explicitly-marked elements
// eliminates the reflow while preserving correct nav-item color switching.

// ── Per-zone type ─────────────────────────────────────────────────────────────

export interface DarkZone {
  left: number;
  right: number;
}

/**
 * Returns true if the horizontal centre of the given element bounds
 * falls inside any of the recorded dark zones.
 */
export function isElementOverDark(
  elementLeft: number,
  elementRight: number,
  darkZones: DarkZone[],
): boolean {
  const centerX = (elementLeft + elementRight) / 2;
  return darkZones.some(
    (zone) => centerX >= zone.left && centerX <= zone.right,
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function useHeaderBehavior() {
  const pathname = usePathname();

  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [headerRect, setHeaderRect] = useState<DOMRect | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  /**
   * Instead of a single boolean, we record the horizontal spans of every
   * dark element that overlaps the header mid-line. Each nav item, link, or
   * icon then independently checks whether its own centre-x falls in any zone.
   */
  const [darkZones, setDarkZones] = useState<DarkZone[]>([]);

  const headerRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollY = useRef(0);
  const lastDarkZonesRef = useRef<DarkZone[]>([]);

  /**
   * Scans every DOM element that geometrically overlaps the header mid-line
   * and collects the horizontal spans of those with a dark background
   * (luminance ≤ gray-700, alpha ≥ 0.5).
   *
   * Each collected span is clamped to the header's own bounding box so that
   * only the portion that actually sits under the header is recorded.
   */
  /**
   * Re-measure the header's bounding rect. Used after any event that
   * may change the header's visual position (resize, scroll, route change,
   * or transform transition).
   */
  const measureHeader = useCallback(() => {
    if (headerRef.current) {
      setHeaderRect(headerRef.current.getBoundingClientRect());
    }
  }, []);

  const checkOverlap = useCallback(() => {
    if (typeof window === 'undefined' || !headerRef.current) return;

    const rect = headerRef.current.getBoundingClientRect();
    const headerY = rect.top + rect.height / 2;
    const headerLeft = rect.left;
    const headerRight = rect.right;

    const candidates = document.querySelectorAll<HTMLElement>(
      '[data-header-dark="true"]',
    );
    const zones: DarkZone[] = [];

    for (const el of candidates) {
      if (headerRef.current.contains(el)) continue;

      const elRect = el.getBoundingClientRect();
      if (elRect.width === 0 || elRect.height === 0) continue;

      if (headerY < elRect.top || headerY > elRect.bottom) continue;

      if (elRect.right < headerLeft || elRect.left > headerRight) continue;

      zones.push({
        left: Math.max(elRect.left, headerLeft),
        right: Math.min(elRect.right, headerRight),
      });
    }

    const prev = lastDarkZonesRef.current;
    if (zones.length !== prev.length) {
      lastDarkZonesRef.current = zones;
      setDarkZones(zones);
      return;
    }
    for (let i = 0; i < zones.length; i++) {
      if (zones[i].left !== prev[i].left || zones[i].right !== prev[i].right) {
        lastDarkZonesRef.current = zones;
        setDarkZones(zones);
        return;
      }
    }
  }, []);

  // ── Menu interaction ────────────────────────────────────────────────────────

  const handleToggle = useCallback((id: string) => {
    setActiveMenu((prev) => (prev === id ? null : id));
  }, []);

  const handleMouseEnter = useCallback((id: string) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setActiveMenu(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  }, []);

  // ── Lifecycle effects ───────────────────────────────────────────────────────

  useEffect(() => {
    setMounted(true);
  }, []);

  // ── Reset state on route change ──────────────────────────────────────────
  //
  // When the user navigates (back, forward, link click), the page scroll
  // resets but isScrolling / lastScrollY / headerRect may be stale.
  // We reset everything and re-measure after a short delay to let the
  // header transition back to its visible position.
  useEffect(() => {
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
    setIsScrolling(false);
    lastScrollY.current = 0;

    // The header may still be mid-transition (-translate-y-[200%] → 0).
    // Wait for the 300ms CSS transition to finish, then re-measure.
    const timer = setTimeout(() => {
      measureHeader();
      checkOverlap();
    }, 350);

    // Also re-measure immediately for the common case where the header
    // is already visible (e.g. user was at the top of the page).
    measureHeader();
    checkOverlap();

    return () => clearTimeout(timer);
  }, [pathname, measureHeader, checkOverlap]);

  // ── Re-measure after scroll-hide transition ────────────────────────────
  //
  // isScrolling toggles translate-y-[-200%] which changes the visual
  // position returned by getBoundingClientRect(). Re-measure after the
  // 300ms CSS transition so the mega menu uses the correct position.
  useEffect(() => {
    const timer = setTimeout(() => {
      measureHeader();
    }, 350);
    return () => clearTimeout(timer);
  }, [isScrolling, measureHeader]);

  const measureHeaderRef = useRef(measureHeader);
  useEffect(() => {
    measureHeaderRef.current = measureHeader;
  }, [measureHeader]);

  useEffect(() => {
    const update = () => {
      measureHeaderRef.current();
      checkOverlapRef.current();
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !headerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveMenu(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const checkOverlapRef = useRef(checkOverlap);
  useEffect(() => {
    checkOverlapRef.current = checkOverlap;
  }, [checkOverlap]);

  const overlapRafId = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setActiveMenu(null);
      const currentScrollY =
        window.pageYOffset || document.documentElement.scrollTop;

      if (currentScrollY <= 50) {
        setIsScrolling(false);
      } else if (currentScrollY > lastScrollY.current) {
        setIsScrolling(true);
      } else if (currentScrollY < lastScrollY.current) {
        setIsScrolling(false);
      }

      lastScrollY.current = currentScrollY;

      if (overlapRafId.current === null) {
        overlapRafId.current = requestAnimationFrame(() => {
          overlapRafId.current = null;
          checkOverlapRef.current();
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (overlapRafId.current !== null) {
        cancelAnimationFrame(overlapRafId.current);
      }
    };
  }, []);

  return {
    activeMenu,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    mounted,
    headerRect,
    headerRef,
    dropdownRef,
    handleToggle,
    handleMouseEnter,
    handleMouseLeave,
    isScrolling,
    darkZones,
  };
}
